import { useEffect, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import * as Sentry from '@sentry/browser';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { OnSubmitType } from 'questionFlow/types';
import QuestionOrganism from 'questionFlow/QuestionOrganism';
import { Button, Spinner } from 'components';
import { useTranslatedValues } from 'hooks/useTranslatedValues';
import { loadOnfidoSdk, MockCompleteData, OnfidoSdkHandle } from 'services/onfidoSdk';

const cmsLocation = '/questions/identity/onfidoStudio';

const translations = {
  title: `${cmsLocation}.title`,
  errorTitle: `${cmsLocation}.errorTitle`,
  errorSubtitle: `${cmsLocation}.errorSubtitle`,
  buttonTextRetry: `${cmsLocation}.buttonTextRetry`,
} as const;

// Operation deliberately not named CreateOnfidoWorkflowRun: codegen's result
// type (<OperationName>Mutation) would collide with the schema's graphene
// payload type CreateOnfidoWorkflowRunMutation in generated/graphql.tsx.
export const CREATE_ONFIDO_WORKFLOW_RUN = gql`
  mutation StartOnfidoWorkflowRun($documentType: OnfidoStudioDocumentType, $nationality: String) {
    createOnfidoWorkflowRun(documentType: $documentType, nationality: $nationality) {
      workflowRunId
      sdkToken
    }
  }
`;

interface CreateOnfidoWorkflowRunData {
  createOnfidoWorkflowRun?: {
    workflowRunId?: string | null;
    sdkToken?: string | null;
  } | null;
}

// Only the stubbed SDK sends an outcome (test environments): django holds the
// run at awaiting_input until something completes it, and which outcome it
// lands on decides what ops sees, so the tester chooses. Named FinishMock…
// rather than CompleteMock… for the codegen collision described above.
export const COMPLETE_MOCK_ONFIDO_WORKFLOW_RUN = gql`
  mutation FinishMockOnfidoWorkflowRun($workflowRunId: String!, $outcome: OnfidoStudioMockOutcome) {
    completeMockOnfidoWorkflowRun(workflowRunId: $workflowRunId, outcome: $outcome) {
      status
    }
  }
`;

const prioritisePrimaryDoc = (primary?: string, secondary?: string): string => {
  if (primary === 'UK_PASSPORT' || secondary !== 'UK_DRIVING_LICENCE') {
    return 'PASSPORT';
  }
  return 'DRIVING_LICENCE';
};
// The values shape is flow-specific; this step only reads the two fields the
// backend forwards to the Studio workflow (which restricts capture to the
// declared document + nationality). A licence journey is recognised by the
// secondary-document selection that routed here (rightToRentIdentity); every
// other route is the passport journey.
export const workflowRunVariables = (values: unknown) => {
  const { nationality, secondaryDocumentOne, primaryDocument } = (values ?? {}) as {
    nationality?: string;
    secondaryDocumentOne?: string;
    primaryDocument?: string;
  };

  return {
    documentType: prioritisePrimaryDoc(primaryDocument, secondaryDocumentOne),
    nationality,
  };
};

export const ONFIDO_CONTAINER_ID = 'onfido-mount';

// Deliberately flow-agnostic (used by both the defaultIdentity and
// rightToRentIdentity flows): only the props the step chrome and submit
// wiring need, so either flow's QuestionComponentProps satisfies it.
interface OnfidoStudioProps {
  onSubmit: OnSubmitType;
  onBack: () => void;
  index: number;
  sectionName: string;
  data?: unknown;
  values?: unknown;
}

const OnfidoStudio = ({ data, values, ...props }: OnfidoStudioProps) => {
  const content = useTranslatedValues(translations);
  const { i18n } = useTranslation();
  const [sdkFailed, setSdkFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const sdkHandleRef = useRef<OnfidoSdkHandle | null>(null);
  // The flow renderer recreates onSubmit every render, but the SDK must init
  // exactly once per workflow run — callbacks read the latest handler from a
  // ref instead of retriggering the init effect.
  const onSubmitRef = useRef(props.onSubmit);
  onSubmitRef.current = props.onSubmit;

  const [createWorkflowRun, { data: runData, error: runError }] = useMutation<
    CreateOnfidoWorkflowRunData
  >(CREATE_ONFIDO_WORKFLOW_RUN, {
    onError: error => Sentry.captureException(error, { tags: { category: 'graphql' } }),
  });

  // Deliberately swallows failures: the stub is enabled independently of
  // django's mock mode, so against a real backend this call is refused and the
  // journey must carry on regardless.
  const [completeMockRun] = useMutation(COMPLETE_MOCK_ONFIDO_WORKFLOW_RUN, {
    onError: () => undefined,
  });

  // Primitive deps: the variables are stable for the lifetime of the step
  // (selection happened before it), so this still runs exactly once.
  const { documentType, nationality } = workflowRunVariables(values);
  useEffect(() => {
    createWorkflowRun({ variables: { documentType, nationality } });
  }, [createWorkflowRun, documentType, nationality]);

  const sdkToken = runData?.createOnfidoWorkflowRun?.sdkToken;
  const workflowRunId = runData?.createOnfidoWorkflowRun?.workflowRunId;

  useEffect(() => {
    if (!sdkToken || !workflowRunId) {
      return;
    }
    let cancelled = false;

    loadOnfidoSdk()
      .then(onfido => {
        if (cancelled) {
          return;
        }
        sdkHandleRef.current = onfido.init({
          token: sdkToken,
          workflowRunId,
          containerId: ONFIDO_CONTAINER_ID,
          language: i18n.language,
          onComplete: data => {
            const submit = () => onSubmitRef.current({ key: 'onfidoStudio', value: 'complete' });
            const { mockOutcome } = (data ?? {}) as MockCompleteData;
            if (!mockOutcome) {
              submit();
              return;
            }
            // Settle the run's outcome before advancing so the dashboard and
            // the agent report reflect the tester's choice immediately.
            completeMockRun({ variables: { workflowRunId, outcome: mockOutcome } })
              .catch(() => undefined)
              .then(submit);
          },
          onError: error => {
            Sentry.captureException(error, { tags: { category: 'onfido-sdk' } });
            setSdkFailed(true);
          },
        });
      })
      .catch(error => {
        Sentry.captureException(error, { tags: { category: 'onfido-sdk' } });
        if (!cancelled) {
          setSdkFailed(true);
        }
      });

    return () => {
      cancelled = true;
      sdkHandleRef.current?.tearDown();
      sdkHandleRef.current = null;
    };
    // i18n.language deliberately excluded: a language switch mid-capture must
    // not tear down the applicant's in-progress Onfido session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkToken, workflowRunId, attempt]);

  const failed = sdkFailed || !!runError;
  const loading = !failed && !workflowRunId;

  const handleRetry = () => {
    setSdkFailed(false);
    setAttempt(current => current + 1);
    if (runError || !workflowRunId) {
      createWorkflowRun({ variables: { documentType, nationality } });
    }
  };

  return (
    <QuestionOrganism
      {...props}
      onSubmit={() => undefined}
      title={failed ? content.errorTitle : content.title}
      subtitle={failed ? content.errorSubtitle : ''}
      data-testid="onfidoStudio"
      isFooterHidden
    >
      {loading && <Spinner />}
      {failed && (
        <Button type="button" styleType="primary" onClick={handleRetry}>
          {content.buttonTextRetry}
        </Button>
      )}
      <div
        id={ONFIDO_CONTAINER_ID}
        data-testid={ONFIDO_CONTAINER_ID}
        className={clsx('w-full max-w-[600px]', failed ? 'hidden' : 'min-h-[600px]')}
      />
    </QuestionOrganism>
  );
};

export default OnfidoStudio;
